import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Archive, ArchiveRestore, ChevronDown, ChevronUp, ExternalLink, Layers, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/queries";
import { limitLines, money } from "@/lib/plans";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlanDialog } from "./PlanDialog";

const Tag = ({ p }) => {
  if (p.archived) return <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-semibold text-zinc-600">Archived</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {p.is_default ? <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-800">Default</span>
        : <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Available</span>}
      {p.featured && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800" data-testid={`plan-featured-${p.id}`}>Most popular</span>}
    </span>
  );
};

const PlanRow = ({ p, onEdit, onToggle, onMove, first, last }) => (
  <TableRow data-testid={`plan-row-${p.id}`} className={p.archived ? "opacity-60" : ""}>
    <TableCell className="w-12 pr-0">
      {!p.archived && (
        <div className="flex flex-col">
          <Button size="icon" variant="ghost" className="h-6 w-6" aria-label="Move plan up" disabled={first} onClick={() => onMove(p, -1)} data-testid={`plan-move-up-${p.id}`}><ChevronUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" aria-label="Move plan down" disabled={last} onClick={() => onMove(p, 1)} data-testid={`plan-move-down-${p.id}`}><ChevronDown className="h-4 w-4" /></Button>
        </div>)}
    </TableCell>
    <TableCell>
      <p className="font-semibold text-slate-900">{p.name}</p>
      {p.display_only && <span className="mt-0.5 inline-block rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700" data-testid={`plan-display-only-${p.id}`}>Show only</span>}
      {p.description && <p className="max-w-xs text-xs text-slate-500">{p.description}</p>}
    </TableCell>
    <TableCell className="font-data text-sm" data-testid={`plan-price-${p.id}`}>{p.price ? <>{money(p.price)}<span className="text-slate-500"> / month</span></> : <span className="text-slate-500">No price</span>}{p.price_text && <span className="block text-xs text-slate-500">Shown as "{p.price_text}"</span>}</TableCell>
    <TableCell className="text-sm">{[...limitLines(p), ...(p.basic ? ["Basic: no maps"] : [])].map((l) => <p key={l} className={l.startsWith("Basic") ? "font-semibold text-amber-700" : ""}>{l}</p>)}</TableCell>
    <TableCell className="font-data text-sm" data-testid={`plan-companies-${p.id}`}>{p.company_count}</TableCell>
    <TableCell data-testid={`plan-status-${p.id}`}><Tag p={p} /></TableCell>
    <TableCell className="text-right whitespace-nowrap">
      {p.stripe_url && <Button asChild size="icon" variant="ghost" aria-label="View in Stripe" title="View in Stripe" data-testid={`plan-stripe-${p.id}`}>
        <a href={p.stripe_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
      </Button>}
      <Button size="icon" variant="ghost" aria-label="Edit plan" onClick={() => onEdit(p)} data-testid={`plan-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
      {!p.is_default && (
        <Button size="icon" variant="ghost" aria-label={p.archived ? "Restore plan" : "Archive plan"} onClick={() => onToggle(p)} data-testid={`plan-${p.archived ? "restore" : "archive"}-${p.id}`}>
          {p.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
        </Button>)}
    </TableCell>
  </TableRow>
);

export const PlanManagementSection = ({ plansState, embedded = false }) => {
  const { data, loading, error, reload, setData } = plansState;
  const [dialog, setDialog] = useState({ open: false, plan: null });

  const active = (data || []).filter((p) => !p.archived);
  const move = async (p, dir) => {
    const list = [...active];
    const i = list.findIndex((x) => x.id === p.id);
    [list[i], list[i + dir]] = [list[i + dir], list[i]];
    const next = [...list, ...data.filter((x) => x.archived)];
    setData(next);
    try {
      await post("/platform/plans/reorder", { ids: next.map((x) => x.id) });
    } catch (e) {
      toast.error(errorMessage(e));
      reload();
    }
  };

  const toggle = async (p) => {
    try {
      await post(`/platform/plans/${p.id}/${p.archived ? "restore" : "archive"}`);
      toast.success(p.archived ? "Plan restored" : "Plan archived. Companies already on it keep it.");
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <section className="space-y-5" data-testid={embedded ? "owner-account-plan-management" : "plans-management"}>
      {embedded ? (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Plan management</h2>
            <p className="mt-1 text-sm text-slate-500">Plans available to companies on their Billing page.</p>
          </div>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setDialog({ open: true, plan: null })} data-testid="add-plan-button"><Plus className="mr-1 h-4 w-4" /> New plan</Button>
        </div>
      ) : <PageHeader eyebrow="Platform" title="Plans" description="Subscription plans companies can choose on their Billing page. Use the arrows to set the order they appear in."
        actions={<Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setDialog({ open: true, plan: null })} data-testid="add-plan-button"><Plus className="mr-1 h-4 w-4" /> New plan</Button>} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows />}
      {data && !data.length && <EmptyState icon={Layers} title="No plans yet" description="Create your first plan." testId="plans-empty" />}
      {data?.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="plans-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="w-12"><span className="sr-only">Order</span></TableHead><TableHead>Plan</TableHead><TableHead>Price</TableHead><TableHead>Limits</TableHead><TableHead>Companies</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{data.map((p) => <PlanRow key={p.id} p={p} onEdit={(plan) => setDialog({ open: true, plan })} onToggle={toggle} onMove={move}
              first={active[0]?.id === p.id} last={active[active.length - 1]?.id === p.id} />)}</TableBody>
          </Table>
        </div>
      )}
      <PlanDialog open={dialog.open} plan={dialog.plan} onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={reload} />
    </section>
  );
};

export default function Plans() {
  const { profile } = useAuth();
  const plansState = useAsync(() => (profile.is_platform_owner ? get("/platform/plans") : Promise.resolve([])), []);
  if (!profile.is_platform_owner) return <Navigate to="/manager" replace />;

  return (
    <div className="space-y-6" data-testid="plans-page">
      <PlanManagementSection plansState={plansState} />
    </div>
  );
}
